import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { t } from 'src/i18n';
import '../../library/components/d-popup.js';
import '../../library/editors/elements/d-checkbox';
import '../../library/editors/elements/d-edit-textarea.js';
import '../../library/editors/elements/d-select-date.js';
import '../../library/editors/elements/d-select-dropdown';
import '../../library/elements/d-section.js';
import '../../library/fields/d-expansion.js';
import './d-new-issue';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import type { DNewIssue } from 'src/content/issues/d-new-issue';
import { isEmptyOrInvalidString } from 'src/utilities/text';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import type { NewIssueEditItem } from './new-issue-edit-item';
import type { NewIssueInput } from './new-issue-input';
import type { NewIssueResult } from './new-issue-result';
import type { DialogActionInput } from '../../library/components/DialogActionInput';

/**
 *
 * USAGE:
 *    d-application
 *
 */
@customElement('new-issue-dialog')
export class NewIssueDialog extends BaseDialog<NewIssueInput, NewIssueResult> {
  @property({ type: Array })
  recipientOptions: SelectDropdownOption[] = [];
  @property({ type: Number })
  organizationId = 0;
  @property({ type: String })
  currentEmployeeUuid: string = '';
  @property({ type: Object })
  newIssueEditItem: NewIssueEditItem = {
    description: '',
    recipient: '',
    classified: false,
    anonymous: false,
    eventDate: '',
    immediateMeasures: '',
    furtherMeasures: '',
    processing: 'DEFAULT',
  };
  @property({ type: Boolean })
  issueRegistered = false;
  @property({ type: Boolean })
  registrationComplete = false;
  width = 680;
  @query('d-new-issue')
  private newIssueElm?: DNewIssue;
  private canProcessIssue: boolean = true;

  protected get calculatedTitle(): string {
    if (this.registrationComplete) {
      return t('issues.newIssueThanksTitle');
    }
    return t('issues.newIssueDialogTitle');
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    if (this.registrationComplete) {
      return [{ name: t('common:close'), action: 'close', main: true }];
    }
    return [
      { name: t('common:cancel'), action: 'cancel' },
      { name: t('issues.newIssueSend'), action: 'send', main: true, disabled: this.sendDisabled },
    ];
  }

  private get sendDisabled() {
    return isEmptyOrInvalidString(this.newIssueEditItem.description) && !this.issueRegistered;
  }

  onNewIssueEditItemChanged(e) {
    e.stopPropagation();
    this.newIssueEditItem = e.detail.value;
  }

  onIssueRegistered() {
    this.issueRegistered = true;
  }

  onRegistrationComplete() {
    this.registrationComplete = true;
  }

  renderBody() {
    if (this.newIssueEditItem) {
      return html`
        <d-new-issue
          .editItem=${this.newIssueEditItem}
          .recipientOptions=${this.recipientOptions}
          .organizationId=${this.organizationId}
          .currentEmployeeUuid=${this.currentEmployeeUuid}
          .issueRegistered=${this.issueRegistered}
          .registrationComplete=${this.registrationComplete}
          .canProcessIssue=${this.canProcessIssue}
          @new-issue-edit-item-changed=${(e) => this.onNewIssueEditItemChanged(e)}
          @issue-registered=${() => this.onIssueRegistered()}
          @registration-complete=${() => this.onRegistrationComplete()}
          @close=${(e) => this.onClose(e)}
        ></d-new-issue>
      `;
    }
    return html``;
  }

  protected async onDispatchAction(disabled: boolean, action: string) {
    if (this.newIssueElm) {
      if (this.registrationComplete) {
        this.newIssueElm.close();
      } else if (this.issueRegistered) {
        if (action === 'cancel') {
          this.newIssueElm.close();
        }
        if (action === 'send') {
          this.newIssueElm.saveAdditionalInformation();
        }
      } else if (action === 'send' && !disabled) {
        this.newIssueElm.newIssue();
      } else if (action === 'cancel') {
        this.newIssueElm.close();
      }
    }
  }

  protected fetchResult(_detail: string | undefined): NewIssueResult {
    return {
      action: 'done',
    };
  }

  protected initializeDialog(input: NewIssueInput) {
    this.organizationId = input.organizationId;
    this.currentEmployeeUuid = input.currentEmployeeUuid;
    this.recipientOptions = input.recipientOptions;
    this.canProcessIssue = input.canProcessIssue;
    this.newIssueEditItem = {
      description: '',
      recipient: input.recipientOptions[0]?.value ?? '',
      classified: false,
      anonymous: false,
      eventDate: '',
      immediateMeasures: '',
      furtherMeasures: '',
      processing: 'DEFAULT',
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'new-issue-dialog': NewIssueDialog;
  }
}
